import React from 'react'
import { Map as ImmutableMap, List } from 'immutable'
import { getValueByUID } from './getValueByUID'
import { ACTION_TYPES } from '../lib/constants/ActionTypes'
import { COLOR_MODE_TYPES } from './constants/ColorModeTypes'

export const staticFieldMap = {
  [ACTION_TYPES.SET_BG]: 'value',
  [ACTION_TYPES.SET_BORDER]: 'value',
  [ACTION_TYPES.SET_COLOR]: 'valueColor',
}

export const COLOR_PREFIXES = {
  BG_COLOR: 'bg_color_',
  BORDER: 'border_',
  COLOR: 'color_',
}

export const colorPrefixesActionMap = {
  [ACTION_TYPES.SET_BG]: COLOR_PREFIXES.BG_COLOR,
  [ACTION_TYPES.SET_BORDER]: COLOR_PREFIXES.BORDER,
  [ACTION_TYPES.SET_COLOR]: COLOR_PREFIXES.COLOR,
}

const HEX_REGREXP = /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/

/** @typedef {'set_color' | 'set_bg_color' | 'set_border_color'} ActionType */

/** @type {Record<ActionType, (action?: ImmutableMap) => string} */
const dynamicColorActionTypeEnum = {
  set_color: (action) => action?.getIn(['valueColor'], '') || '',
  set_bg_color: (action) => action?.getIn(['value'], '') || '',
  set_border_color: (action) => action?.getIn(['value'], '') || '',
}

/**
 * @param {string | undefined}
 * @returns {string}
 */
export const stripColorHash = (value) => {
  if (!value) return ''

  if (value.startsWith('#')) {
    return value.slice(1)
  }
  return value
}

/**
 * @param {string}
 * @returns {string}
 */
export const addColorHash = (value) => {
  if (value.startsWith('#')) return value

  return `#${value}`
}

/** @param {ImmutableMap<string, string>} action */
const resolveStaticColorValue = (action) => {
  const actionType = action.get('type', '')

  /** @type {((action: ImmutableMap<string, string>) => string) | undefined} */
  const extractor = dynamicColorActionTypeEnum[actionType]

  if (!extractor) return ''

  return extractor(action)
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export const isValidColor = (value) => {
  if (!value || typeof value !== 'string') return false

  const color = stripColorHash(value)

  if (HEX_REGREXP.test(color)) return true

  if (typeof document !== 'undefined') {
    const option = new Option()
    option.style.color = value
    return option.style.color !== ''
  }

  return true
}

/**
 * @param {ImmutableMap<string, string>} action
 * @param {ImmutableMap} state
 * @returns {string}
 */
const resolveDynamicColorValue = (action, state) => {
  const fromField = action.getIn(['from'])

  if (!fromField || !ImmutableMap.isMap(fromField)) {
    console.warn('[DynamicColor] fromField is missing or not a Map', {
      fromField,
      isMap: ImmutableMap.isMap(fromField),
      actionKeys: action?.toJS ? Object.keys(action.toJS()) : 'not immutable',
    })
    return ''
  }

  const variablePath = fromField.get('value')

  if (!variablePath) {
    console.warn('[DynamicColor] variablePath is missing', fromField.toJS())
    return ''
  }

  const color = getValueByUID(state, variablePath)

  if (!color) {
    console.warn('[DynamicColor] Color value is empty for path:', variablePath)
    return ''
  }

  const colorString = String(color).trim()

  if (isValidColor(colorString)) {
    return HEX_REGREXP.test(stripColorHash(colorString)) ? addColorHash(colorString) : colorString
  } else {
    console.warn('[DynamicColor] Invalid color value:', colorString, 'for path:', variablePath)
    return ''
  }
}

/**
 * @param {ImmutableMap} action
 * @param {ImmutableMap} state
 */
export const resolveColorValue = (action, state) => {
  if (!action || !ImmutableMap.isMap(action)) return ''

  /** @type {'static' | 'dynamic'} */
  const colorMode = action.get('colorMode', COLOR_MODE_TYPES.STATIC)

  if (!isDynamicColorMode(colorMode)) {
    return resolveStaticColorValue(action)
  }

  return resolveDynamicColorValue(action, state)
}

/**
 * @param {ImmutableMap} action
 * @param {ImmutableMap} state
 * @returns {string}
 */
export const getRawColorValue = (action, state) => {
  const mode = action.get('colorMode', COLOR_MODE_TYPES.STATIC)
  const type = action.get('type', '')

  if (isDynamicColorMode(mode)) {
    return resolveColorValue(action, state)
  }

  return action.get(staticFieldMap[type], '')
}

/**
 * @param {ImmutableMap} action
 * @param {ImmutableMap} state
 */
export const getColorValueFromAction = (action, state) => {
  const colorValue = getRawColorValue(action, state)

  if (!colorValue) return ''

  const type = action.get('type', '')
  return `${colorPrefixesActionMap[type]}${colorValue}`
}

const STYLE_PROP_MAP = {
  [ACTION_TYPES.SET_BG]: 'backgroundColor',
  [ACTION_TYPES.SET_COLOR]: 'color',
  [ACTION_TYPES.SET_BORDER]: 'borderColor',
}

/**
 * @param {List} actionTarget
 * @param {ImmutableMap} state
 * @returns {React.CSSProperties}
 */
export const buildStyleObjectFromActions = (actionTarget, state) => {
  if (!actionTarget || !List.isList(actionTarget) || !actionTarget.size) {
    return {}
  }

  return actionTarget.reduce((styleObject, action) => {
    if (!ImmutableMap.isMap(action)) return styleObject

    const type = action.get('type', '')

    if (type === 'hide') {
      return { ...styleObject, display: 'none' }
    }
    const styleProp = STYLE_PROP_MAP[type]
    if (!styleProp) return styleObject

    const colorValue = getRawColorValue(action, state)

    if (colorValue) {
      return { ...styleObject, [styleProp]: colorValue }
    }

    return styleObject
  }, {})
}

// TODO: (jonathan.a) - remove the 2nd parameter when all places in the code are aligned with the color actions
/**
 * @description - we added the 2nd paramter since
 * we didn't want to change places in the code
 * that where out of scope in the task,
 * and not because the functionality does not exist
 * @param {{ actionType: string, withBorderColor: boolean }}
 * @returns {boolean}
 */
export const isColorAction = ({ actionType, withBorderColor = false }) => {
  const base_color_actions = [ACTION_TYPES.SET_COLOR, ACTION_TYPES.SET_BG]

  if (withBorderColor) {
    base_color_actions.push(ACTION_TYPES.SET_BORDER)
  }
  return base_color_actions.includes(actionType)
}

/**
 * @param {string} mode
 * @returns {boolean}
 */
export const isDynamicColorMode = (mode) => mode === 'dynamic'

export const conditionalColorStyleMap = {
  color: 'color',
  backgroundColor: 'backgroundColor',
  borderColor: 'borderColor',
}
